import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Autocomplete, MenuItem, TextField } from "@mui/material";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import TablePaginationComponent from "../../../components/TablePagination";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { EVENT_TYPES, eventText, eventTone, fmtDateTime, saGet, TableSkeleton } from "../shared";
import styles from "../superadmin.module.css";

const TYPE_LABEL = (t) => t.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Every billing change on the platform: who did what, when
const ActivityList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);

  const type = searchParams.get("type") || "all";
  const companyId = searchParams.get("company_id") || "";
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const page = parseInt(searchParams.get("page") || "0", 10);
  const perPage = parseInt(searchParams.get("per_page") || "50", 10);

  const { websiteData } = useWebsite();
  useSiteBranding("Activity log", websiteData);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "" || value === null || value === "all") next.delete(key); else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    saGet("company-options").then((response) => {
      if (response.data.status === "SUCCESS") setCompanies(response.data.data);
    }).catch(() => { });
  }, []);

  useEffect(() => {
    setLoading(true);
    saGet("activity", { type, company_id: companyId, from, to, page: page + 1, per_page: perPage }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setRows(response.data.data.data);
        setTotal(response.data.data.total);
      }
    }).catch(() => setLoading(false));
  }, [type, companyId, from, to, page, perPage]);

  const selectedCompany = companies.find((c) => String(c.id) === companyId) || null;

  return (<div className={`${styles.Page}`}>
    <PageHeader title="Activity Log" trail={[{ label: "Platform", to: "/super-admin/dashboard" }]} />

    <div className={`${styles.Toolbar}`}>
      <Autocomplete
        size="small"
        options={companies}
        value={selectedCompany}
        getOptionLabel={(o) => o?.company_name || ""}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(e, v) => setParam("company_id", v ? String(v.id) : "")}
        onInputChange={(e, value, reason) => {
          if (reason === "input") {
            saGet("company-options", { search: value }).then((response) => {
              if (response.data.status === "SUCCESS") setCompanies(response.data.data);
            });
          }
        }}
        renderInput={(params) => <TextField {...params} label="Company" />}
        sx={{ minWidth: 240 }}
        className={`${styles.Filter}`}
      />
      <TextField className={`${styles.Filter}`} size="small" select label="Event" value={type} onChange={(e) => setParam("type", e.target.value)}>
        <MenuItem value="all">All events</MenuItem>
        {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{TYPE_LABEL(t)}</MenuItem>)}
      </TextField>
      <TextField className={`${styles.Filter}`} size="small" type="date" label="From" value={from} onChange={(e) => setParam("from", e.target.value)} InputLabelProps={{ shrink: true }} />
      <TextField className={`${styles.Filter}`} size="small" type="date" label="To" value={to} onChange={(e) => setParam("to", e.target.value)} InputLabelProps={{ shrink: true }} />
    </div>

    {loading && <TableSkeleton rows={10} />}

    {!loading && (<div className={`${styles.TableWrap}`}>
      <table className={`${styles.Table}`}>
        <thead><tr><th>When</th><th>Event</th><th>Company</th><th>Invoice</th><th>By</th></tr></thead>
        <tbody>
          {rows.map((e) => (<tr key={e.id}>
            <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(e.created_at)}</td>
            <td><span className={`${styles.EventCell} ${eventTone(e.type)}`}>{eventText(e)}</span></td>
            <td>{e.company_id ? <Link to={`/super-admin/companies/${e.company_id}?tab=activity`} className={`${styles.RowLink}`}>{e.company_name}</Link> : <span className={`${styles.Muted}`}>Platform</span>}</td>
            <td>{e.invoice_id ? <Link to={`/super-admin/invoices/${e.invoice_id}`} className={`${styles.RowLink}`}>{e.invoice_number}</Link> : "—"}</td>
            <td>{e.actor_name || "System"}</td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", padding: 30 }}>No activity matches these filters.</td></tr>}
        </tbody>
      </table>
      {total > 0 && <div className={`${styles.Pagination}`}>
        <table><tbody><tr>
          <TablePaginationComponent count={total} colSpan={5} page={page} rowsPerPage={perPage} handleChangePage={(e, p) => setParam("page", String(p))} handleChangeRowsPerPage={(e) => setParam("per_page", e.target.value)} />
        </tr></tbody></table>
      </div>}
    </div>)}
  </div>);
}

export default withSuperAdminAuth(ActivityList);
